// src/contexts/LikesContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';
import api from '../api/axios';
import { useAuth } from './AuthContext';

// 🔑 shared key helper (same logic as mobile)
export const likeKeyFromRow = (row: any): string =>
  String(
    row?.postId ??
    row?.sourceId ??
    row?.mediaId ??
    row?.originalId ??
    row?.details?.postId ??
    row?._id
  );

type LikesCtx = {
  likedIds: Set<string>;
  likeCounts: Map<string, number>;
  likePost: (postKey: string) => Promise<number | undefined>;
  unlikePost: (postKey: string) => Promise<number | undefined>;
  refreshLikes: () => Promise<void>;
  loadingLikes: boolean;
};

const Ctx = createContext<LikesCtx | null>(null);

// Small helper to normalize like-counts from various API shapes
const parseCount = (res: any): number | undefined => {
  const raw = res?.data;
  const n = Number(
    raw?.likes ??
    raw?.totalLikes ??
    raw?.post?.likes ??
    raw?.data?.likes
  );
  return Number.isFinite(n) ? n : undefined;
};

export const LikesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, user } = useAuth();
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [likeCounts, setLikeCounts] = useState<Map<string, number>>(new Map());
  const [loadingLikes, setLoadingLikes] = useState(false);

  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : undefined),
    [token]
  );

  // ✅ backend is the only source of truth for likedIds + likeCounts
  const refreshLikes = useCallback(async () => {
    if (!user?._id || !headers) return;

    setLoadingLikes(true);
    try {
      const res = await api.get('/likes/liked/mine', {
        headers,
        params: { limit: 2000 },
      });

      const rows =
        Array.isArray(res.data) ? res.data :
        Array.isArray(res.data?.items) ? res.data.items :
        Array.isArray(res.data?.posts) ? res.data.posts :
        [];

      const ids = new Set<string>();
      const counts = new Map<string, number>();

      for (const row of rows) {
        const key = likeKeyFromRow(row);
        ids.add(key);

        const n = Number(
          row?.likes ??
          row?.totalLikes ??
          row?.post?.likes ??
          row?.data?.likes
        );
        if (Number.isFinite(n)) {
          counts.set(key, n);
        }
      }

      setLikedIds(ids);
      setLikeCounts(counts);
    } catch (err) {
      console.error('❌ refreshLikes error:', err);
    } finally {
      setLoadingLikes(false);
    }
  }, [user?._id, headers]);

  // 🔁 Reset + reload likes on login / logout / token change
  useEffect(() => {
    const shouldFetch = !!user?._id && !!token;
    setLikedIds(new Set());
    setLikeCounts(new Map());
    if (shouldFetch) {
      refreshLikes();
    }
  }, [user?._id, token, refreshLikes]);

  // 🔁 Refresh when tab/window becomes active again
  useEffect(() => {
    if (!user?._id || !token) return;

    const onFocus = () => {
      refreshLikes();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshLikes();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user?._id, token, refreshLikes]);

  // 👉 Use POST response for count; refresh likedIds/likeCounts in background
  const likePost = useCallback(
    async (postKey: string): Promise<number | undefined> => {
      const id = String(postKey);
      const res = await api.post(`/likes/${id}/like`, null, { headers });
      const count = parseCount(res);

      // kick off sync but don't block UI on it
      refreshLikes().catch(() => {});
      return count;
    },
    [headers, refreshLikes]
  );

  const unlikePost = useCallback(
    async (postKey: string): Promise<number | undefined> => {
      const id = String(postKey);
      const res = await api.post(`/likes/${id}/unlike`, null, { headers });
      const count = parseCount(res);

      // kick off sync but don't block UI on it
      refreshLikes().catch(() => {});
      return count;
    },
    [headers, refreshLikes]
  );

  const value = useMemo(
    () => ({
      likedIds,
      likeCounts,
      likePost,
      unlikePost,
      refreshLikes,
      loadingLikes,
    }),
    [likedIds, likeCounts, likePost, unlikePost, refreshLikes, loadingLikes]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useLikes = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useLikes must be used inside <LikesProvider>');
  return ctx;
};
